/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useVehicles } from '../../hooks/useVehicles';
import { soldVehiclesMock, SoldVehicle } from '../../mocks/soldVehicles';
import { useSEOHead } from '../../hooks/useSEOHead';
import SoldHero from './components/SoldHero';
import SoldFilters from './components/SoldFilters';
import SoldVehicleCard from './components/SoldVehicleCard';
import SoldCta from './components/SoldCta';
import PuplandiaPartnershipBanner from './components/PuplandiaPartnershipBanner';
import FindMeOneModal from './components/FindMeOneModal';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

export default function SoldVehiclesPage() {
  useSEOHead({
    title: 'Sold Vehicles — Gone But Not Forgotten | Rockhound',
    description: 'Browse Rockhound Auto\'s sold vehicles archive. See our track record of transparent, no-pressure used car sales serving Warrenton, Astoria, Seaside, and the Oregon Coast.',
    keywords: 'sold vehicles Rockhound Auto, past sales Warrenton OR, used cars sold Oregon Coast',
    canonicalUrl: 'https://rockhoundauto.com/sold-vehicles',
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
    noindex: true,
  });

  const liveVehicleData = useVehicles();
  const liveVehicles = liveVehicleData?.vehicles ?? [];
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(12);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<{ make: string; model: string; year: number; bodyType: string; } | null>(null);
  const [milestoneDismissed, setMilestoneDismissed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem('sold_first_milestone_dismissed');
    if (dismissed === 'true') {
      setMilestoneDismissed(true);
    }
  }, []);

  const allSoldVehicles: SoldVehicle[] = useMemo(() => {
    const liveSold: SoldVehicle[] = (liveVehicles ?? []).filter((v) => v.status === 'sold').map((v) => ({
      id: v.id, name: v.name, make: v.make, model: v.model, year: v.year, price: v.price, soldPrice: v.price,
      mileage: v.mileage, bodyType: v.bodyType, fuelType: v.fuelType, transmission: v.transmission,
      drivetrain: v.drivetrain, exteriorColor: v.exteriorColor, soldDate: new Date().toISOString().split('T')[0],
      daysOnLot: 0, image: v.images && v.images.length > 0 ? v.images[0] : v.image, buyerCity: v.buyer_city || 'Oregon Coast',
    }));
    return [...liveSold, ...soldVehiclesMock];
  }, [liveVehicles]);

  const makes = useMemo(() => Array.from(new Set(allSoldVehicles.map((v) => v.make))).sort(), [allSoldVehicles]);
  const bodyTypes = useMemo(() => Array.from(new Set(allSoldVehicles.map((v) => v.bodyType))).sort(), [allSoldVehicles]);

  const filtered = useMemo(() => {
    let result = [...allSoldVehicles];
    if (selectedMake) result = result.filter((v) => v.make === selectedMake);
    if (selectedBodyType) result = result.filter((v) => v.bodyType === selectedBodyType);
    switch (sortBy) {
      case 'price-high': result.sort((a, b) => b.soldPrice - a.soldPrice); break;
      case 'price-low': result.sort((a, b) => a.soldPrice - b.soldPrice); break;
      case 'fastest': result.sort((a, b) => a.daysOnLot - b.daysOnLot); break;
      default: result.sort((a, b) => new Date(b.soldDate).getTime() - new Date(a.soldDate).getTime());
    }
    return result;
  }, [allSoldVehicles, selectedMake, selectedBodyType, sortBy]);

  const displayed = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Show milestone banner only when exactly 1 vehicle sold AND not dismissed
  const showMilestoneBanner = allSoldVehicles.length === 1 && !milestoneDismissed;

  const handleDismissMilestone = () => {
    localStorage.setItem('sold_first_milestone_dismissed', 'true');
    setMilestoneDismissed(true);
  };

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Sold Vehicles',
          item: 'https://rockhoundauto.com/sold-vehicles',
        },
      ],
    };

    injectJsonLd('schema-sold-vehicles-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-sold-vehicles-breadcrumb');
    };
  }, []);

  useEffect(() => {
    injectFAQSchema(
      'schema-sold-vehicles-faq',
      pageFaqs.soldVehicles,
      'https://rockhoundauto.com/sold-vehicles#faqpage'
    );

    return () => {
      removeFAQSchema('schema-sold-vehicles-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Sold Vehicles' }]} />
      {/* Visually hidden H1 for SEO scanners — visible H1 is inside SoldHero */}
      <h1 className="sr-only">Sold Vehicles — Rockhound Auto Track Record</h1>
      <SoldHero totalSoldDisplayed={allSoldVehicles.length} />
      <PuplandiaPartnershipBanner />
      <div className="bg-white border-b border-gray-200 sticky top-[120px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <SoldFilters makes={makes} bodyTypes={bodyTypes} selectedMake={selectedMake} selectedBodyType={selectedBodyType} sortBy={sortBy} onMakeChange={setSelectedMake} onBodyTypeChange={setSelectedBodyType} onSortChange={setSortBy} resultCount={filtered.length} />
        </div>
      </div>
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {allSoldVehicles.length === 0 ? (
            /* ── True empty state: no sales yet ── */
            <div className="max-w-2xl mx-auto text-center py-20">
              <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-car-line text-4xl text-teal-500"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nothing Here Yet — <span className="text-teal-600">And That's the Point</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We open on <strong>March 31, 2026</strong>. Every vehicle that eventually lands on this page will be a real deal — a real car, a real buyer, a real price. No inflated "was $X, now $Y" theater. No invented testimonials. Just an honest record of what sold and what it sold for.
              </p>
              <p className="text-base text-gray-500 mb-10 leading-relaxed">
                When the first car sells, it goes here. Until then, this page is a promise: we'd rather show you nothing than show you something fake.
              </p>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                  <i className="ri-shield-check-line text-teal-600"></i>
                  What you'll see here after we open:
                </h3>
                <ul className="space-y-2 text-sm text-teal-800">
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 flex-shrink-0"></i>The actual sold price — not the asking price</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 flex-shrink-0"></i>How many days it sat on the lot</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 flex-shrink-0"></i>The buyer's city (no names, just the town)</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 flex-shrink-0"></i>A "Find Me One Like This" button so you can get in line</li>
                </ul>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/inventory" className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-car-line"></i> Browse Current Inventory
                </Link>
                <Link to="/deal-radar" className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:border-teal-400 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-notification-line"></i> Get Deal Alerts
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* ── First Sale Celebration Banner ── */}
              {showMilestoneBanner && (
                <div className="relative bg-gradient-to-br from-amber-50 via-teal-50 to-amber-50 border-2 border-teal-300 rounded-2xl p-8 mb-10 overflow-hidden shadow-lg">
                  {/* Confetti-style decorative elements */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
                    <div className="absolute top-4 left-8 w-3 h-3 bg-teal-500 rounded-full"></div>
                    <div className="absolute top-12 right-16 w-2 h-2 bg-amber-500 rounded-full"></div>
                    <div className="absolute bottom-8 left-20 w-4 h-4 bg-teal-400 rounded-full"></div>
                    <div className="absolute bottom-16 right-12 w-3 h-3 bg-amber-400 rounded-full"></div>
                    <div className="absolute top-20 left-1/3 w-2 h-2 bg-teal-600 rounded-full"></div>
                    <div className="absolute top-8 right-1/4 w-3 h-3 bg-amber-600 rounded-full"></div>
                  </div>

                  <button
                    onClick={handleDismissMilestone}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-full transition-colors cursor-pointer"
                    aria-label="Dismiss celebration banner"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>

                  <div className="relative text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full mb-4 shadow-lg">
                      <i className="ri-trophy-line text-3xl text-white"></i>
                    </div>
                    
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">
                      🎉 The First One's In the Books
                    </h2>
                    
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      Real buyer. Real price. Real deal. This is the start of something honest — every vehicle on this page from here on out will be part of an actual track record, not a marketing story.
                    </p>

                    <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full border border-teal-200 shadow-sm">
                      <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                      <span className="text-sm font-semibold text-gray-700">
                        No fake testimonials. No inflated stats. Just what actually happened.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {displayed.length === 0 ? (
                /* ── Filtered empty state: results exist but filters hide them ── */
                <div className="text-center py-16">
                  <i className="ri-filter-off-line text-6xl text-gray-300 mb-4 block"></i>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No sold vehicles match your filters</h3>
                  <p className="text-gray-500 mb-6">Try broadening your search — there are {allSoldVehicles.length} sold vehicle{allSoldVehicles.length !== 1 ? 's' : ''} in the archive.</p>
                  <button onClick={() => { setSelectedMake(''); setSelectedBodyType(''); }} className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-refresh-line mr-2"></i> Reset Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayed.map((vehicle, idx) => (
                      <SoldVehicleCard
                        key={vehicle.id}
                        vehicle={vehicle}
                        priority={idx === 0}
                        onFindSimilar={(v) => { setSelectedVehicle(v); setModalOpen(true); }}
                      />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="text-center mt-10">
                      <button onClick={() => setVisibleCount((prev) => prev + 12)} className="inline-flex items-center px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-teal-500 hover:text-teal-600 transition-all whitespace-nowrap cursor-pointer">
                        <i className="ri-arrow-down-line mr-2"></i> Load More ({filtered.length - visibleCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
      <SoldCta />
      <FindMeOneModal isOpen={modalOpen} onClose={() => setModalOpen(false)} vehicle={selectedVehicle} />
    </>
  );
}